import { useState, useEffect } from 'react';
import { useGetBoolSettingQuery } from '../../../services/settingsApi';
import { printAgentService } from '../../../services/printAgentService';
import { bluetoothPrintService } from '../../../services/bluetoothPrintService';
import { useCloudPrint } from '../../../hooks/useCloudPrint';
import { shouldUseCloudPrint } from '../../../utils/cloudPrintRoute';
import { Order } from '../../../types/orders';
import { ProductOrderItem } from '../../../types/products';

/**
 * Everything that happens AFTER an order is created/updated: print the comanda
 * (cloud print → print agent → bluetooth). This was duplicated, near
 * line-for-line, in TakeOutOrder and DeliveryOrder — the only differences were
 * the area label and the customer reference.
 *
 * El envío al KDS ya NO vive aquí: lo hace el backend dentro de la transacción que
 * guarda los renglones (ver IKitchenOrderFactory). La impresión sí se queda del lado
 * del cliente porque depende de hardware local (agente, Bluetooth) que el servidor no
 * alcanza; su fallo es visible en el momento y siempre se puede reimprimir.
 */

export interface KitchenSendParams {
  /** 'Para Llevar' | 'Domicilio' — KDS area and ticket label prefix */
  areaName: string;
  /** Customer name shown as the KDS "table" and in the ticket label */
  customerName: string;
}

/** ShowOnScreen controls KDS only; printTicket controls the paper comanda */
const printableItems = (items: ProductOrderItem[]) =>
  items.filter(item => {
    if (!item.productionCenters || item.productionCenters.length === 0) return true;
    return item.productionCenters.some(pc => pc.printTicket);
  });

export const useKitchenPipeline = () => {
  const { data: printComandasSetting } = useGetBoolSettingQuery('ImprimirComandas');
  const { data: cloudPrintSetting } = useGetBoolSettingQuery('CloudPrintEnabled');
  // Nube solo con servidor y con la orden ya en él: ver shouldUseCloudPrint.
  const cloudPrintSettingOn = cloudPrintSetting?.value ?? false;
  const { printKitchenOrder: cloudPrintKitchenOrder } = useCloudPrint();

  const [printAgentAvailable, setPrintAgentAvailable] = useState(false);
  const [bluetoothAvailable, setBluetoothAvailable] = useState(false);
  useEffect(() => {
    const checkPrintServices = async () => {
      setBluetoothAvailable(bluetoothPrintService.isAvailable());
      setPrintAgentAvailable(await printAgentService.isAvailable());
    };
    checkPrintServices();
  }, []);

  const printBatches = async (
    order: Order,
    tableLabel: string,
    batches: any[][],
  ) => {
    // Se decide aquí, con la orden ya creada: si cayó a la cola offline lleva folio
    // "SIN RED" y va al Print Agent aunque el botón se haya pulsado con red.
    if (shouldUseCloudPrint(cloudPrintSettingOn, order)) {
      for (const batch of batches) {
        await cloudPrintKitchenOrder({
          orderId: order.id,
          tableLabel,
          waiterName: 'Sistema',
          items: batch.map(item => ({
            productId: item.productId,
            productName: item.productName,
            quantity: item.quantity,
            variants: (item.variants || []).map((v: any) => v.variantName),
            notes: item.notes,
          })),
        });
      }
    } else if (printAgentAvailable) {
      for (const batch of batches) {
        const result = await printAgentService.printKitchenOrder(order, tableLabel, 'Sistema', batch as any);
        if (!result.success) console.warn('Print Agent error:', result.error);
      }
    } else if (bluetoothAvailable) {
      for (const batch of batches) {
        const result = await bluetoothPrintService.printKitchenOrder(order, tableLabel, 'Sistema', batch as any);
        if (!result.success) console.warn('Bluetooth print error:', result.error);
      }
    }
  };

  /**
   * Create mode: print the comanda. El envío al KDS ya no pasa por aquí — lo hace el
   * backend dentro de la transacción que crea la orden (ver IKitchenOrderFactory). Antes
   * era un POST aparte cuyo fallo se tragaba con un console.warn, y encima emparejaba los
   * renglones locales con los persistidos a ciegas (producto+cantidad+notas): cuando el
   * match fallaba mandaba el item sin orderItemId y el KDS lo escondía al filtrar por
   * centro. Los ids ahora los pone quien acaba de crearlos.
   */
  const sendCreatedOrder = async (
    order: Order,
    localItems: ProductOrderItem[],
    { areaName, customerName }: KitchenSendParams,
  ) => {
    if (localItems.length === 0) return;

    const shouldPrintComandas = printComandasSetting?.value ?? true;
    if (!shouldPrintComandas) return;

    try {
      const itemsToPrint = printableItems(localItems);
      if (itemsToPrint.length === 0) return;

      const regularItems = itemsToPrint.filter(item => !item.dailyMenuSelections?.length);
      const dailyMenuItems = itemsToPrint.filter(item => item.dailyMenuSelections?.length);

      // Group identical regular items (same product/variants/notes) into one line
      const regularGroupMap = new Map<string, { item: ProductOrderItem; qty: number }>();
      for (const item of regularItems) {
        const variantsKey = item.selectedVariants.map(v => v.name).sort().join(',');
        const selectionsKey = (item.variantSelections || []).map(v => `${v.variantId}:${v.quantity}`).sort().join(',');
        const key = `${item.productName}|${variantsKey}|${selectionsKey}|${item.notes || ''}`;
        const existing = regularGroupMap.get(key);
        if (existing) {
          existing.qty += item.quantity;
        } else {
          regularGroupMap.set(key, { item, qty: item.quantity });
        }
      }
      const regularOrderItems = Array.from(regularGroupMap.values()).map(({ item, qty }) => ({
        id: '',
        orderId: order.id,
        productId: item.productId,
        productName: item.productName,
        quantity: qty,
        unitPrice: item.unitPrice,
        total: qty * item.unitPrice,
        variants: item.selectedVariants.map(v => ({
          variantId: v.id,
          variantName: v.name,
          priceModifier: v.priceModifier,
        })),
        notes: item.notes,
        isCancelled: false,
        createdAt: new Date().toISOString(),
      }));

      // Daily menu: aggregate per course ("SOPA: 2× Consomé, 1× Azteca"), notes on the last course
      const courseAgg = new Map<string, { courseName: string; sortOrder: number; options: Map<string, number> }>();
      for (const item of dailyMenuItems) {
        for (const sel of item.dailyMenuSelections || []) {
          if (!sel.optionName) continue;
          let courseData = courseAgg.get(sel.courseName);
          if (!courseData) {
            courseData = { courseName: sel.courseName, sortOrder: sel.sortOrder ?? 0, options: new Map() };
            courseAgg.set(sel.courseName, courseData);
          }
          const currentQty = courseData.options.get(sel.optionName) || 0;
          courseData.options.set(sel.optionName, currentQty + item.quantity);
        }
      }

      const dailyMenuNotes = dailyMenuItems
        .filter(item => item.notes)
        .map(item => item.notes!);
      const uniqueDailyMenuNotes = Array.from(new Set(dailyMenuNotes)).join(' | ');

      const sortedCourses = Array.from(courseAgg.values()).sort((a, b) => a.sortOrder - b.sortOrder);
      const dailyMenuOrderItems = sortedCourses.map((course, idx) => {
        const totalQty = Array.from(course.options.values()).reduce((sum, q) => sum + q, 0);
        const optionsList = Array.from(course.options.entries())
          .map(([name, qty]) => qty > 1 ? `${qty}× ${name}` : name);
        const isLast = idx === sortedCourses.length - 1;
        return {
          id: '',
          orderId: order.id,
          productId: '',
          productName: course.courseName.toUpperCase(),
          quantity: totalQty,
          unitPrice: 0,
          total: 0,
          variants: optionsList.map(opt => ({ variantId: '', variantName: opt, priceModifier: 0 })),
          notes: (isLast && uniqueDailyMenuNotes) ? uniqueDailyMenuNotes : undefined as string | undefined,
          isCancelled: false,
          createdAt: new Date().toISOString(),
        };
      });

      const ticketBatches: any[][] = [];
      if (regularOrderItems.length > 0) ticketBatches.push(regularOrderItems);
      if (dailyMenuOrderItems.length > 0) ticketBatches.push(dailyMenuOrderItems);

      await printBatches(order, `${areaName} - ${customerName || ''}`, ticketBatches);
    } catch (printError) {
      console.warn('Print failed (continuing anyway):', printError);
    }
  };

  /**
   * Edit mode: only the items added in this session get printed. El envío al KDS lo hace el
   * backend al guardar la edición, que es el único que sabe con certeza qué renglón es nuevo:
   * aquí se calculaba diffeando ids contra la orden previa, y el PUT los regeneraba todos.
   */
  const sendEditedOrder = async (
    updatedOrder: Order,
    _existingOrder: Order | undefined,
    newLocalItems: ProductOrderItem[],
    { areaName, customerName }: KitchenSendParams,
  ) => {
    if (newLocalItems.length === 0) return;

    const shouldPrint = printComandasSetting?.value ?? true;
    if (!shouldPrint) return;

    try {
      const itemsToPrint = printableItems(newLocalItems);
      if (itemsToPrint.length === 0) return;

      const printItems = itemsToPrint.map(item => ({
        id: '',
        orderId: updatedOrder.id,
        productId: item.productId,
        productName: item.productName,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        total: item.quantity * item.unitPrice,
        variants: item.selectedVariants.map(v => ({
          variantId: v.id,
          variantName: v.name,
          priceModifier: v.priceModifier,
        })),
        notes: item.notes,
        isCancelled: false,
        createdAt: new Date().toISOString(),
      }));

      await printBatches(updatedOrder, `${areaName} - ${customerName || ''}`, [printItems]);
    } catch (printError) {
      console.warn('Failed to print on edit (continuing):', printError);
    }
  };

  return { sendCreatedOrder, sendEditedOrder };
};
